
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Star, Calendar, Languages } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { LanguageBadgeList } from '@/components/ui/language-badge';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface PsychologistProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  psychologist: any;
  onBookConsultation?: (psychologistId: string) => void;
  onViewFullProfile?: (psychologistId: string) => void;
}

const PsychologistProfileModal = ({ 
  isOpen, 
  onClose, 
  psychologist,
  onBookConsultation,
  onViewFullProfile 
}: PsychologistProfileModalProps) => {
  const navigate = useNavigate();

  // Fetch real-time pricing data usando nova estrutura
  const { data: pricingData } = useQuery({
    queryKey: ['psychologist-pricing-modal', psychologist?.id],
    queryFn: async () => {
      if (!psychologist?.id) return null;
      
      const { data, error } = await supabase
        .from('psychologist_pricing')
        .select('price_30min, price_60min, currency, is_active')
        .eq('psychologist_id', psychologist.id)
        .eq('is_active', true)
        .single();
      
      if (error) {
        console.error('Error fetching pricing:', error);
        return null;
      }
      
      return data;
    },
    enabled: !!psychologist?.id,
  });

  if (!psychologist) return null;

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleViewFullProfile = () => {
    onClose(); // Close the modal first
    if (onViewFullProfile) {
      onViewFullProfile(psychologist.id);
    } else {
      // Use the correct URL from router: /psychologist-details/:id
      navigate(`/psychologist-details/${psychologist.id}`);
    }
  };

  const handleBookConsultation = () => {
    onClose(); // Close the modal first
    if (onBookConsultation) {
      onBookConsultation(psychologist.id);
    } else {
      navigate(`/schedule/step1?psychologistId=${psychologist.id}`);
    }
  };

  // Get the primary price (60min preferido, senão 30min, senão fallback)
  const getPrimaryPrice = () => {
    if (pricingData) {
      return pricingData.price_60min || pricingData.price_30min || 280.00;
    }
    
    // Fallback to default price
    return psychologist.price || 280;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-background border-border">
        <DialogHeader>
          <DialogTitle className="text-foreground">Perfil do Psicólogo</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Header with photo and basic info */}
          <div className="flex flex-col md:flex-row gap-4 items-center md:items-start">
            <Avatar className="h-24 w-24">
              <AvatarImage src={psychologist.photoUrl || psychologist.photo_url} alt={psychologist.name} />
              <AvatarFallback className="bg-primary/10 text-primary text-lg">
                {psychologist.name?.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1 text-center md:text-left space-y-2">
              <h3 className="text-2xl font-bold text-foreground">{psychologist.name}</h3>
              <div className="flex items-center justify-center md:justify-start gap-1">
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                <span className="font-medium text-foreground">{psychologist.rating || psychologist.average_rating || '5.0'}</span>
                <span className="text-muted-foreground text-sm">({psychologist.total_reviews || '150'}+ avaliações)</span>
              </div>
              <div className="text-xl font-bold text-primary">
                R$ {getPrimaryPrice()}
                <span className="text-sm text-muted-foreground font-normal"> / sessão</span>
              </div>
            </div>
          </div>

          {/* Bio */}
          <div>
            <h4 className="font-semibold mb-2 text-foreground">Sobre o Profissional</h4>
            <p className="text-muted-foreground">
              {psychologist.bio || psychologist.short_bio || 'Psicólogo experiente em atendimento online com foco em bem-estar e saúde mental.'}
            </p>
          </div>

          {/* Specialties */}
          <div>
            <h4 className="font-semibold mb-2 text-foreground">Especialidades</h4>
            <div className="flex flex-wrap gap-2">
              {psychologist.specialties && psychologist.specialties.length > 0 ? (
                psychologist.specialties.map((specialty: string, index: number) => (
                  <Badge key={index} className="bg-blue-500 hover:bg-blue-600 text-white">
                    {specialty}
                  </Badge>
                ))
              ) : (
                <p className="text-muted-foreground">Nenhuma especialidade registrada</p>
              )}
            </div>
          </div>

          {/* Languages */}
          <div>
            <h4 className="font-semibold mb-2 flex items-center gap-2 text-foreground">
              <Languages className="h-4 w-4" />
              Idiomas Fluentes
            </h4>
            {psychologist.languages && psychologist.languages.length > 0 ? (
              <LanguageBadgeList 
                languages={psychologist.languages}
                maxVisible={6}
                showTooltip={false}
                variant="secondary"
                size="md"
              />
            ) : (
              <p className="text-muted-foreground">Nenhum idioma registrado</p>
            )}
          </div>

          {/* Pricing Options (nova estrutura global) */}
          {pricingData && (
            <div>
              <h4 className="font-semibold mb-2 text-foreground">Opções de Duração</h4>
              <div className="grid grid-cols-2 gap-3">
                {pricingData.price_30min && (
                  <div className="text-center p-3 border border-border rounded-lg bg-card">
                    <div className="font-semibold text-card-foreground">30 min</div>
                    <div className="text-primary font-bold">R$ {pricingData.price_30min}</div>
                  </div>
                )}
                {pricingData.price_60min && (
                  <div className="text-center p-3 border border-border rounded-lg bg-card">
                    <div className="font-semibold text-card-foreground">60 min</div>
                    <div className="text-primary font-bold">R$ {pricingData.price_60min}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Education */}
          {psychologist.education && (
            <div>
              <h4 className="font-semibold mb-2 text-foreground">Formação</h4>
              <p className="text-muted-foreground">{psychologist.education}</p>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3 pt-4">
            <Button 
              onClick={handleBookConsultation}
              className="flex-1 bg-green-500 hover:bg-green-600 text-white"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Agendar Consulta
            </Button>
            <Button 
              variant="outline" 
              className="flex-1 border-border bg-background text-foreground hover:bg-accent"
              onClick={handleViewFullProfile}
            >
              Ver Perfil Completo
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PsychologistProfileModal;
